/**
 * Traduz as mensagens cruas da API do Google Ad Manager para um diagnóstico
 * legível, com causa e sugestão de correção.
 *
 * As assinaturas cobertas aqui foram levantadas a partir dos erros reais
 * gravados em custom_targeting_values.error_message.
 */

/** Tamanho máximo aceito pelo GAM no campo `name` de um Custom Targeting Value. */
export const GAM_VALUE_NAME_MAX_LENGTH = 40;

/** Tamanho máximo aceito pelo GAM no campo `name` de uma Custom Targeting Key. */
export const GAM_KEY_NAME_MAX_LENGTH = 20;

export type GamErrorTone = 'limit' | 'invalid' | 'duplicate' | 'transient' | 'unknown';

export interface GamErrorDiagnosis {
    /** Código curto, exibido como badge. */
    code: string;
    /** Resumo em uma linha. */
    title: string;
    /** O que aconteceu. */
    detail: string;
    /** O que fazer a respeito. */
    hint: string;
    tone: GamErrorTone;
    /** Se o value pode voltar a funcionar sozinho numa nova tentativa. */
    retryable: boolean;
    /** Valor que disparou o erro, quando o GAM informa. */
    trigger?: string;
    /** Mensagem original, preservada para investigação. */
    raw: string;
}

interface Rule {
    match: RegExp;
    build: (ctx: { trigger?: string; name?: string }) => Omit<GamErrorDiagnosis, 'raw' | 'trigger'>;
}

/**
 * Ordem importa: uma mesma mensagem pode conter mais de um erro do GAM.
 * A primeira regra que casar define a causa principal — a que precisa ser
 * resolvida para o value passar.
 */
const RULES: Rule[] = [
    {
        match: /CUSTOM_TARGETING_VALUES_FOR_KEY_LIMIT_REACHED/,
        build: () => ({
            code: 'LIMITE_DA_KEY',
            title: 'A key atingiu o limite de values do GAM',
            detail:
                'O Google Ad Manager não aceita mais values nesta key. Todo value novo ' +
                'passa a falhar até que values antigos sejam removidos.',
            hint:
                'Reduza a cardinalidade da key (agrupe valores ou pare de enviar esta ' +
                'dimensão) ou limpe values que não são mais usados no GAM.',
            tone: 'limit',
            retryable: false,
        }),
    },
    {
        match: /REPORTABLE_TYPE_ON_KEY_VALUES_EXCEED_LIMIT/,
        build: () => ({
            code: 'LIMITE_REPORTABLE',
            title: 'Limite de values reportáveis excedido',
            detail:
                'A key foi criada com reportableType = ON, o que impõe um teto menor de ' +
                'values do que o limite normal.',
            hint:
                'Avalie desligar o reporting nesta key no GAM se ela não for usada em ' +
                'relatórios, ou reduza a quantidade de values.',
            tone: 'limit',
            retryable: false,
        }),
    },
    {
        match: /VALUE_NAME_INVALID_LENGTH/,
        build: ({ trigger, name }) => {
            const subject = trigger ?? name;
            const len = subject?.length;
            return {
                code: 'NOME_MUITO_LONGO',
                title: `Nome acima de ${GAM_VALUE_NAME_MAX_LENGTH} caracteres`,
                detail: len
                    ? `O GAM aceita no máximo ${GAM_VALUE_NAME_MAX_LENGTH} caracteres no nome do value. Este tem ${len}.`
                    : `O GAM aceita no máximo ${GAM_VALUE_NAME_MAX_LENGTH} caracteres no nome do value.`,
                hint:
                    'O valor precisa ser encurtado na origem antes de ser enviado. ' +
                    'Parâmetros como fbclid nunca cabem neste limite.',
                tone: 'invalid',
                retryable: false,
            };
        },
    },
    {
        match: /KEY_NAME_INVALID_LENGTH/,
        build: ({ trigger, name }) => {
            const subject = trigger ?? name;
            const len = subject?.trim().length;
            return {
                code: 'NOME_DE_KEY_LONGO',
                title: `Nome de key acima de ${GAM_KEY_NAME_MAX_LENGTH} caracteres`,
                detail: len
                    ? `O GAM aceita no máximo ${GAM_KEY_NAME_MAX_LENGTH} caracteres no nome de uma key. Este tem ${len}.`
                    : `O GAM aceita no máximo ${GAM_KEY_NAME_MAX_LENGTH} caracteres no nome de uma key.`,
                hint:
                    'Renomeie a key com um identificador curto. O nome longo pode ficar ' +
                    'apenas no display name, que aceita bem mais caracteres.',
                tone: 'invalid',
                retryable: false,
            };
        },
    },
    {
        match: /KEY_NAME_INVALID_CHARS/,
        build: () => ({
            code: 'NOME_DE_KEY_INVALIDO',
            title: 'Nome de key com caracteres não aceitos',
            detail:
                'O GAM não aceita espaços nem alguns caracteres especiais no nome de uma key.',
            hint:
                'Use apenas letras, números e underscore — por exemplo, ' +
                '"utm_campaign_hour" no lugar de "Utm Campaign Hour".',
            tone: 'invalid',
            retryable: false,
        }),
    },
    {
        match: /NotNullError\.NULL/,
        build: () => ({
            code: 'CAMPO_OBRIGATORIO',
            title: 'Campo obrigatório vazio',
            detail:
                'O GAM recebeu um campo obrigatório nulo ou em branco. Costuma acontecer ' +
                'quando o nome tem espaços sobrando nas pontas.',
            hint: 'Verifique se nome e display name estão preenchidos e sem espaços no início ou fim.',
            tone: 'invalid',
            retryable: false,
        }),
    },
    {
        match: /VALUE_NAME_INVALID_CHARS/,
        build: () => ({
            code: 'CARACTERES_INVALIDOS',
            title: 'Nome com caracteres não aceitos',
            detail: 'O GAM rejeita alguns caracteres no nome do value (vírgulas e aspas, por exemplo).',
            hint: 'Normalize o valor na origem, removendo os caracteres não suportados.',
            tone: 'invalid',
            retryable: false,
        }),
    },
    {
        match: /UniqueError\.NOT_UNIQUE|VALUE_NAME_DUPLICATE|DuplicateValueError/,
        build: () => ({
            code: 'JA_EXISTE_NO_GAM',
            title: 'O value já existe no GAM',
            detail:
                'O GAM recusou a criação porque já existe um value com este nome na key. ' +
                'Na prática o targeting funciona — o que faltou foi vincular o ID existente aqui.',
            hint: 'Reprocessar este value deve resolver: basta buscar o ID já cadastrado no GAM.',
            tone: 'duplicate',
            retryable: true,
        }),
    },
    {
        match: /CONCURRENT_MODIFICATION/,
        build: () => ({
            code: 'CONCORRENCIA',
            title: 'Conflito de escrita simultânea',
            detail: 'O GAM recusou a operação porque a mesma entidade estava sendo alterada em paralelo.',
            hint: 'Falha temporária — uma nova tentativa normalmente resolve.',
            tone: 'transient',
            retryable: true,
        }),
    },
    {
        match: /ServerError\.SERVER_ERROR|Connection aborted|RemoteDisconnected|Timeout|timed out/i,
        build: () => ({
            code: 'FALHA_TEMPORARIA',
            title: 'Falha de comunicação com o GAM',
            detail: 'A chamada não chegou a ser concluída (erro interno do GAM ou queda de conexão).',
            hint: 'Falha temporária — uma nova tentativa normalmente resolve.',
            tone: 'transient',
            retryable: true,
        }),
    },
    {
        match: /Erro ao conectar GAM/,
        build: () => ({
            code: 'CONEXAO_GAM',
            title: 'Não foi possível conectar ao GAM',
            detail: 'Falha ao inicializar o cliente do Ad Manager para a network deste value.',
            hint: 'Verifique as credenciais de service account e se a network está acessível.',
            tone: 'transient',
            retryable: true,
        }),
    },
    {
        match: /Key .* não encontrada/,
        build: () => ({
            code: 'KEY_AUSENTE',
            title: 'Key não encontrada',
            detail: 'O value aponta para uma key que não existe mais na base.',
            hint: 'O value ficou órfão e pode ser removido.',
            tone: 'invalid',
            retryable: false,
        }),
    },
];

/** Extrai o `trigger:'...'` que o GAM anexa à mensagem, quando presente. */
function extractTrigger(raw: string): string | undefined {
    const m = raw.match(/trigger:'([\s\S]*?)'(?:[,\]]|$)/);
    return m ? m[1] : undefined;
}

/**
 * Converte a mensagem crua em um diagnóstico. Sempre devolve algo — mensagens
 * desconhecidas voltam como `DESCONHECIDO` com o texto original preservado.
 */
export function diagnoseGamError(
    raw?: string | null,
    ctx: { name?: string } = {}
): GamErrorDiagnosis | null {
    if (!raw || !raw.trim()) return null;

    const trigger = extractTrigger(raw);

    for (const rule of RULES) {
        if (rule.match.test(raw)) {
            return { ...rule.build({ trigger, name: ctx.name }), trigger, raw };
        }
    }

    return {
        code: 'DESCONHECIDO',
        title: 'Erro não catalogado',
        detail: 'O GAM devolveu um erro que ainda não foi mapeado.',
        hint: 'Use a mensagem original abaixo para investigar.',
        tone: 'unknown',
        retryable: false,
        trigger,
        raw,
    };
}

/** Classes de cor por tipo de erro, para manter o visual consistente. */
export const TONE_STYLES: Record<GamErrorTone, { badge: string; panel: string; icon: string }> = {
    limit: {
        badge: 'bg-amber-100 text-amber-800 border-amber-200',
        panel: 'bg-amber-50 border-amber-200',
        icon: 'text-amber-600',
    },
    invalid: {
        badge: 'bg-rose-100 text-rose-800 border-rose-200',
        panel: 'bg-rose-50 border-rose-200',
        icon: 'text-rose-600',
    },
    duplicate: {
        badge: 'bg-sky-100 text-sky-800 border-sky-200',
        panel: 'bg-sky-50 border-sky-200',
        icon: 'text-sky-600',
    },
    transient: {
        badge: 'bg-slate-100 text-slate-700 border-slate-200',
        panel: 'bg-slate-50 border-slate-200',
        icon: 'text-slate-500',
    },
    unknown: {
        badge: 'bg-slate-100 text-slate-700 border-slate-200',
        panel: 'bg-slate-50 border-slate-200',
        icon: 'text-slate-500',
    },
};
